// API service for backend communication
// const API_BASE_URL = 'http://127.0.0.1:8000';
// const API_BASE_URL = 'http://10.30.1.100';
const API_BASE_URL = 'https://numericalaiapi.fetchcfd.com/pyfr';



class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Helper method to handle API responses
  async handleResponse(response) {
    if (!response.ok) {
      // Check for 401 Unauthorized responses
      if (response.status === 401) {
        // Clear any stored tokens
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        
        // Redirect to signin page
        window.location.href = '/signin';
        return; // Exit early to prevent further processing
      }
      
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // Helper method to get headers
  getHeaders(includeAuth = false) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (includeAuth) {
      const token = localStorage.getItem('access_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    return headers;
  }

  // User registration
  async registerUser(userData) {
    try {
      const response = await fetch(`${this.baseURL}/user/create`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          first_name: userData.firstName,
          last_name: userData.lastName,
          email: userData.email,
          password: userData.password,
          organization: userData.organization,
          role: userData.role,
          research_area: userData.researchArea,
          credit_expiry_notification: userData.credit_expiry_notification,
          newsletter_subscription: userData.newsletter_subscription,
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // User login
  async loginUser(username, password, turnstileToken) {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      formData.append('turnstile_token', turnstileToken);

      const response = await fetch(`${this.baseURL}/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData)
      });

      const data = await this.handleResponse(response);
      
      // Store token in localStorage
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('token_type', data.token_type);
      }
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Get current user profile
  async getCurrentUser() {
    try {
      const response = await fetch(`${this.baseURL}/user/details`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get user error:', error);
      throw error;
    }
  }

  // Update user profile
  async updateUserProfile(userData) {
    try {
      const response = await fetch(`${this.baseURL}/user/details`, {
        method: 'PUT',
        headers: this.getHeaders(true),
        body: JSON.stringify(userData)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  }

  // Get user statistics
  async getUserStats() {
    try {
      const response = await fetch(`${this.baseURL}/users/me/stats`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get user stats error:', error);
      throw error;
    }
  }

  // Get all jobs
  async getAllJobs() {
    try {
      const response = await fetch(`${this.baseURL}/jobs`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get jobs error:', error);
      throw error;
    }
  }

  // Get job status
  async getJobStatus(jobId) {
    try {
      const response = await fetch(`${this.baseURL}/job-status/${jobId}`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get job status error:', error);
      throw error;
    }
  }

  // Get job logs
  async getJobLogs(jobId) {
    try {
      const response = await fetch(`${this.baseURL}/job-log/${jobId}`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get job logs error:', error);
      throw error;
    }
  }


  // Delete job
  async cancelJob(jobId) {
    try {
      const response = await fetch(`${this.baseURL}/job-cancel/${jobId}`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Cancel job error:', error);
      throw error;
    }
  }

  async deleteJob(jobId) {
    try {
      const response = await fetch(`${this.baseURL}/job-delete/${jobId}`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Delete job error:', error);
      throw error;
    }
  }


  // Check job eligibility
  async checkJobEligibility() {
    try {
      const response = await fetch(`${this.baseURL}/user/check-eligibility`, {
        method: 'POST',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Check job eligibility error:', error);
      throw error;
    }
  }
  
  // Get purchase summary
  async getPurchaseSummary() {
    try {
      const response = await fetch(`${this.baseURL}/subscription/purchase-summary`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get purchase summary error:', error);
      throw error;
    }
  }

  // Get purchase history
  async getPurchaseHistory() {
    try {
      const response = await fetch(`${this.baseURL}/subscription/purchase-history`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get purchase history error:', error);
      throw error;
    }
  }


  // Submit simulation task
  async submitTask(simSoftware, zipFile, jobName = null) {
    try {
      const formData = new FormData();
      formData.append('sim_software', simSoftware);
      formData.append('zip_file', zipFile);
      if (jobName) {
        formData.append('job_name', jobName);
      }

      const response = await fetch(`${this.baseURL}/post-task`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Submit task error:', error);
      throw error;
    }
  }

  // Download job results
  async downloadJobResults(jobId) {
    try {
      const response = await fetch(`${this.baseURL}/download-results/${jobId}`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // The endpoint now returns a JSON response with the file URL
      const result = await response.json();
      return result; // Should contain the file URL
    } catch (error) {
      console.error('Download results error:', error);
      throw error;
    }
  }
  
  async downloadZipFile(jobId) {
    try {
      const response = await fetch(`${this.baseURL}/download-results/${jobId}`, {
        method: 'GET',
        headers: this.getHeaders(true),
      });
  
      if (!response.ok) {
        throw new Error(`Network error: ${response.status} ${response.statusText}`);
      }
  
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
  
      return { blobUrl: url };
    } catch (error) {
      console.error('Download ZIP file error:', error);
      throw error;
    }
  }

  // Get subscription plans
  async getSubscriptionPlans() {
    try {
      const response = await fetch(`${this.baseURL}/subscriptions`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get subscription plans error:', error);
      throw error;
    }
  }

  // Subscribe to a plan
  async subscribeToPlan(subscriptionId, autoRenew = true) {
    try {
      const response = await fetch(`${this.baseURL}/users/me/subscription`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify({
          subscription_id: subscriptionId,
          auto_renew: autoRenew
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Subscribe to plan error:', error);
      throw error;
    }
  }

  // Start simulation
  async startSimulation(formData) {
    try {
      const response = await fetch(`${this.baseURL}/post-task`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: formData // FormData for file upload
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Start simulation error:', error);
      throw error;
    }
  }

  // Logout user
  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_type');
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  }

  // Get stored token
  getToken() {
    return localStorage.getItem('access_token');
  }

  // Get user job statistics
  async getUserJobStats() {
    try {
      const response = await fetch(`${this.baseURL}/jobs/user/stats`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get user job stats error:', error);
      throw error;
    }
  }

  // Get job estimated time
  async getJobEstimatedTime(jobId) {
    try {
      const response = await fetch(`${this.baseURL}/job/estimated-time/${jobId}`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get job estimated time error:', error);
      throw error;
    }
  }

  // Get user jobs
  async getUserJobs() {
    try {
      const response = await fetch(`${this.baseURL}/jobs/user`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get user jobs error:', error);
      throw error;
    }
  }

  // Get user queue stats
  async getUserQueueStats() {
    try {
      const response = await fetch(`${this.baseURL}/jobs/user/queue-stats`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get user queue stats error:', error);
      throw error;
    }
  }

  // Get subscription details
  async getSubscriptionDetails() {
    try {
      const response = await fetch(`${this.baseURL}/subscription/details`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get subscription details error:', error);
      throw error;
    }
  }

  // Get user consumed hours
  async getUserConsumedHours() {
    try {
      const response = await fetch(`${this.baseURL}/jobs/user/consumed-hours`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get user consumed hours error:', error);
      throw error;
    }
  }

  // Get user details
  async getUserDetails() {
    try {
      const response = await fetch(`${this.baseURL}/user/details`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get user details error:', error);
      throw error;
    }
  }

  // Send contact message
  async sendContactMessage(contactData) {
    try {
      const response = await fetch(`${this.baseURL}/user/contact-us`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify(contactData)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Send contact message error:', error);
      throw error;
    }
  }

  // Create subscription checkout session
  async createSubscriptionCheckout(subscriptionData) {
    try {
      const response = await fetch(`${this.baseURL}/subscription/create-checkout`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify(subscriptionData)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Create subscription checkout error:', error);
      throw error;
    }
  }

  // Cancel subscription
  async cancelSubscription() {
    try {
      const response = await fetch(`${this.baseURL}/subscription/cancel`, {
        method: 'POST',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Cancel subscription error:', error);
      throw error;
    }
  }

  // Create customer portal link
  async createCustomerPortalLink() {
    try {
      const response = await fetch(`${this.baseURL}/subscription/create-customer-portal-link`, {
        method: 'POST',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Create customer portal link error:', error);
      throw error;
    }
  }


  // Validate promotion code
  async validatePromotionCode(promotionCode) {
    try {
      const response = await fetch(`${this.baseURL}/subscription/validate-promotion-code`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify({ promotion_code: promotionCode })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Validate promotion code error:', error);
      throw error;
    }
  }

  // Delete user account
  async deleteUserAccount(deleteData) {
    try {
      const response = await fetch(`${this.baseURL}/user/delete-account`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify(deleteData)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Delete user account error:', error);
      throw error;
    }
  }

  // Reset password request
  async resetPasswordRequest(email) {
    try {
      const response = await fetch(`${this.baseURL}/user/reset-password-request`, {
        method: 'POST',
        headers: this.getHeaders(false), // No auth needed for password reset request
        body: JSON.stringify({ email })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Reset password request error:', error);
      throw error;
    }
  }

  // Reset password with token
  async resetPassword(resetData) {
    try {
      const response = await fetch(`${this.baseURL}/user/reset-password`, {
        method: 'POST',
        headers: this.getHeaders(false), // No auth needed for password reset
        body: JSON.stringify(resetData)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  // Validate task code
  async validateTask(fileContents) {
    try {
      const response = await fetch(`${this.baseURL}/validate-task`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify({
          file_contents: fileContents
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Validate task error:', error);
      throw error;
    }
  }

  // Validate Python code (basic validation)
  async validatePython(fileContents) {
    try {
      const response = await fetch(`${this.baseURL}/validate-python`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify({
          file_contents: fileContents
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Validate Python error:', error);
      throw error;
    }
  }

  // Diagnose job errors
  async diagnoseJob(jobId) {
    try {
      const response = await fetch(`${this.baseURL}/job-log/diagnose/${jobId}`, {
        method: 'GET',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Diagnose job error:', error);
      throw error;
    }
  }

  // Update user preferences
  async updateUserPreferences(preferences) {
    try {
      console.log(preferences);
      const response = await fetch(`${this.baseURL}/user/preferences`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify(preferences)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Update user preferences error:', error);
      throw error;
    }
  }

  // Validate Python code check (Step 1)
  async validatePythonCheck(fileContents) {
    try {
      const response = await fetch(`${this.baseURL}/validate/python-check`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify({
          file_contents: fileContents
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Validate Python check error:', error);
      throw error;
    }
  }

  // Validate MFC code check (Step 2)
  async validateMFCCodeCheck(fileContents) {
    try {
      const response = await fetch(`${this.baseURL}/validate/mfc-code-check`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify({
          file_contents: fileContents
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Validate MFC code check error:', error);
      throw error;
    }
  }

  // Validate system check (Step 3)
  async validateSystemCheck(fileContents) {
    try {
      const response = await fetch(`${this.baseURL}/validate/system-check`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify({
          file_contents: fileContents
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Validate system check error:', error);
      throw error;
    }
  }

  // Validate grid check (Step 4)
  async validateGridCheck(fileContents) {
    try {
      const response = await fetch(`${this.baseURL}/validate/grid-check`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify({
          file_contents: fileContents
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Validate grid check error:', error);
      throw error;
    }
  }

  // Validate AI check (Step 5)
  async validateAICheck(fileContents) {
    try {
      const response = await fetch(`${this.baseURL}/validate/ai-check`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify({
          file_contents: fileContents
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Validate AI check error:', error);
      throw error;
    }
  }

  // Build multipart FormData for preflight endpoints.
  // `options` is an optional map of extra flags the backend understands.
  buildPreflightFormData(zipFile, options = {}) {
    const formData = new FormData();
    formData.append('zip_file', zipFile);
    Object.entries(options).forEach(([key, value]) => {
      formData.append(key, String(value));
    });
    return formData;
  }

  // Streaming preflight validation. Returns the raw Response so the caller can
  // read the Server-Sent-Events body (response.body.getReader()). Does NOT go
  // through handleResponse because the payload is a stream, not JSON.
  async validatePreflight(formData) {
    const response = await fetch(`${this.baseURL}/validate/preflight`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const err = new Error(`Preflight request failed with status ${response.status}`);
      err.status = response.status;
      throw err;
    }

    return response;
  }

  // AI preflight recommendations (non-streaming). Re-runs preflight on the ZIP
  // and returns { steps, overall, recommendations }.
  async fetchPreflightRecommendations(zipFile, options = {}, signal = null) {
    const formData = this.buildPreflightFormData(zipFile, options);
    const fetchOptions = {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    };
    if (signal) {
      fetchOptions.signal = signal;
    }

    const response = await fetch(`${this.baseURL}/validate/preflight-recommendations`, fetchOptions);

    if (!response.ok) {
      const err = new Error(`Preflight recommendations failed with status ${response.status}`);
      err.status = response.status;
      throw err;
    }

    return response.json();
  }

  // Check free tier eligibility
  async checkFreeTierEligibility() {
    try {
      const response = await fetch(`${this.baseURL}/user/check-free-tier-eligibility`, {
        method: 'POST',
        headers: this.getHeaders(true)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Check free tier eligibility error:', error);
      throw error;
    }
  }

  async autoFixJob(jobId, signal = null) {
    const options = {
      method: 'GET',
      headers: this.getHeaders(true),
    };
    
    // Add signal if provided for request cancellation
    if (signal) {
      options.signal = signal;
    }
    
    const response = await fetch(`${this.baseURL}/job-log/auto-fix/${jobId}`, options);
    
    if (!response.ok) {
      throw new Error(`Failed to get auto-fix for job: ${response.statusText}`);
    }
    
    return response.json();
  }

  // Acknowledge waiver before purchase
  async acknowledgeWaiver(purchasePlan, textAcknowledged) {
    try {
      const response = await fetch(`${this.baseURL}/user/acknowledge-waiver`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify({
          purchase_plan: purchasePlan,
          text_acknowledged: textAcknowledged
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Waiver acknowledgment error:', error);
      throw error;
    }
  }

  // Book demo
  async bookDemo(demoData) {
    try {
      const response = await fetch(`${this.baseURL}/book-demo`, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify(demoData)
      });
  
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Book demo error:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService; 